import {FormField, FormFieldProps, FormFieldType} from 'platform/components';

import {FieldPath} from 'react-hook-form';

import {TestIdProps} from 'shared/web';

import {BaseVehicle} from '../../../core/types/BaseVehicle';
import {useStepFieldRegistration} from '../../hooks/useStepFieldRegistration';
import {useVehicleFormContext} from '../../hooks/useVehicleFormContext';
import {FileField, FileFieldProps} from '../fields/FileField';

// Extend FormFieldType to include custom vehicle form field types
export type VehicleFormFieldType = FormFieldType | 'file';

// Union type for field props - either FormField props or FileField props
export type FieldProps<
  T extends VehicleFormFieldType = VehicleFormFieldType,
  K extends BaseVehicle = BaseVehicle,
> = T extends 'file'
  ? FileFieldProps<K> & TestIdProps & {name: FieldPath<K>; type: T}
  : Omit<FormFieldProps<K, T extends FormFieldType ? T : never>, 'control'> &
      TestIdProps & {
        name: FieldPath<K>;
        type: T;
      };

/**
 * Field primitive - enhanced FormField that integrates with vehicle form context
 *
 * @example
 * <Field name="power" type="number" suffix="kW" label="Power" />
 * <Field name="fuelType" type="choice" options={fuelOptions} />
 * <Field name="notes" type="textarea" placeholder="Additional notes..." />
 * <Field name="certificate" type="file" label="Certificate" documentNote="Certificate" />
 */
export function Field<
  K extends BaseVehicle = BaseVehicle,
  T extends VehicleFormFieldType = VehicleFormFieldType,
>(props: FieldProps<T, K>) {
  const {name, type, 'data-testid': dataTestId, ...restProps} = props;

  const {control, baseTestId, mandatoryFields} = useVehicleFormContext();
  useStepFieldRegistration(name);

  // Handle custom field type: file
  if (type === 'file') {
    return (
      <FileField
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(restProps as any)}
        control={control}
        data-testid={dataTestId || `${name}-${baseTestId}`}
      />
    );
  }

  // TypeScript cannot reconcile the complex union types in FormField's ControlProps
  // with our generic wrapper. The 'as any' is necessary to bypass this limitation
  // while maintaining full FormField functionality and type inheritance.
  return (
    <FormField<K, T extends FormFieldType ? T : never>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(restProps as any)}
      control={control}
      type={type as T extends FormFieldType ? T : never}
      name={name}
      data-testid={dataTestId || `${name}-${baseTestId}`}
      isRequired={props.isRequired || mandatoryFields.includes(name)}
      isRecommended={mandatoryFields.includes(name)}
    />
  );
}
